import { SEMButtonProps } from '@workboard/ui-relationships';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import KrRelationships, { KrRelationshipsProps } from './KrRelationships';
import { CoauthorDialog } from './CoauthorDialog';
import { RbrSummaryGenAi, type IRbrSummaryProps } from './RbrSummaryGenAi';
import CoauthorSummary from './CoauthorSummary';
import SEMButton from './SEMButton';
import WsRelationships from './WsRelationships';
import ListBuilder from './Lists';
import { ManagerLens, type TManagerLensDrawerProps } from './ManagerLens';
import ConfidenceFlag from './ConfidenceFlag';
import i18n from '../i18n';
import ObjectiveRelationships, { ObjectiveRelationshipsProps } from './ObjectiveRelationships';
import { resolveKrRelationsRoot } from './kr-relations-roots';

function initKrRelationsComponent(
  domId: string,
  metric: KrRelationshipsProps['metric'],
  eventProperties: KrRelationshipsProps['eventProperties'],
) {
  const mountEl = document.getElementById(domId);
  if (!mountEl) {
    console.log(`mountId does not exists ${domId}`);
    return;
  }

  resolveKrRelationsRoot(domId, mountEl).render(
    <I18nextProvider i18n={i18n}>
      <KrRelationships metric={metric} eventProperties={eventProperties} />
    </I18nextProvider>,
  );
}
function initObjectiveRelationsComponent(domId: string, objectiveId: ObjectiveRelationshipsProps['objectiveId']) {
  const mountId = document.getElementById(domId);
  if (mountId) {
    const root = ReactDOM.createRoot(mountId);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <ObjectiveRelationships objectiveId={objectiveId} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.log(`mountId does not exists ${domId}`);
  }
}

function initOkrCoauthorComponent(domId: string) {
  if (!window.wbConfig.coauthorEnabled) return;
  const mountPoint = document.getElementById(domId);
  if (mountPoint) {
    const root = ReactDOM.createRoot(mountPoint);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <CoauthorDialog />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.error(`mountId does not exist ${domId}`);
  }
}

function initRbrSummaryComponent(domId: string, props: IRbrSummaryProps) {
  if (!window.wbConfig.rbrSummaryGenAiEnabled) return;

  const mountPoint = document.getElementById(domId);

  if (mountPoint) {
    const root = ReactDOM.createRoot(mountPoint);

    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <RbrSummaryGenAi {...props} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.error(`mountId does not exist ${domId}`);
  }
}

function initCoauthorSummaryComponent(domId: string, props: any) {
  const mountId = document.getElementById(domId);
  if (mountId) {
    const root = ReactDOM.createRoot(mountId);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <CoauthorSummary {...props} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.log(`mountId does not exists ${domId}`);
  }
}

function initListBuilderComponent(
  domId: string,
  onSave: (id: number) => void,
  onCancel: () => void,
  listId?: number,
  resourceType?: string,
) {
  const mountPoint = document.getElementById(domId);
  if (!mountPoint) throw new Error(`Invalid mount point ${domId}`);
  const root = ReactDOM.createRoot(mountPoint);
  root.render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <ListBuilder
          listId={listId}
          onSave={onSave}
          onCancel={onCancel}
          resourceType={resourceType}
        />
      </I18nextProvider>
    </React.StrictMode>,
  );
  return () => {
    root.unmount();
  };
}

function initManagerLensComponent(domId: string, props: TManagerLensDrawerProps) {
  if (!window.wbConfig.isManagerLensEnabled) return;

  const mountPoint = document.getElementById(domId);

  if (mountPoint) {
    const root = ReactDOM.createRoot(mountPoint);

    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <ManagerLens {...props} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.error(`mountId does not exist ${domId}`);
  }
}

function initSEMComponent(domId: string, props: SEMButtonProps) {
  if (!window?.wbConfig?.isSemEnabled) return;

  const mountId = document.getElementById(domId);
  if (mountId) {
    const root = ReactDOM.createRoot(mountId);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <SEMButton {...props} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.log(`mountId does not exists ${domId}`);
  }
}

const initWsRelationsComponent = async function (domId: string, props: any) {
  const mountId = document.getElementById(domId);
  if (mountId) {
    const root = ReactDOM.createRoot(mountId);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <WsRelationships {...props} />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.log(`mountId does not exists ${domId}`);
  }
};

const initConfidenceFlagComponent = async function (
  domId: string,
  params: {
    confidence?: 'High' | 'Medium' | 'Low' | 'No Rating';
    comment?: string;
    commentAuthorFullName?: string;
    commentDate?: string;
    anchorEl?: HTMLElement | null | undefined;
  },
) {
  const mountId = document.getElementById(domId);
  if (mountId) {
    const root = ReactDOM.createRoot(mountId);
    root.render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <ConfidenceFlag
            confidence={params.confidence}
            comment={params.comment}
            commentAuthorFullName={params.commentAuthorFullName}
            commentDate={params.commentDate}
            anchorEl={params.anchorEl}
          />
        </I18nextProvider>
      </React.StrictMode>,
    );
  } else {
    console.log(`mountId does not exists ${domId}`);
  }
};

window.initKrRelationsComponent = initKrRelationsComponent;
window.initOkrCoauthorComponent = initOkrCoauthorComponent;
window.initRbrSummaryComponent = initRbrSummaryComponent;
window.initCoauthorSummaryComponent = initCoauthorSummaryComponent;
window.initListBuilderComponent = initListBuilderComponent;
window.initSEMComponent = initSEMComponent;
window.initWsRelationsComponent = initWsRelationsComponent;
window.initManagerLensComponent = initManagerLensComponent;
window.initConfidenceFlagComponent = initConfidenceFlagComponent;
window.initObjectiveRelationsComponent = initObjectiveRelationsComponent;
